import * as amplitude from "@amplitude/analytics-browser";
import { Flex, Tag, Text, Tooltip } from "@chakra-ui/react";
import { Form, Formik } from "formik";
import { FunctionComponent, useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GenericError } from "src/components/Feedback/GenericError";
import { useSearchParamsRetainingHash } from "src/components/Providers/EnrollmentPeriodProvider";
import {
  LOTTERY_AND_OFFERS_DISABLED_MESSAGE,
  MESSAGE_TEMPLATE_CATEGORIES,
  MESSAGE_TEMPLATES,
  MessageTemplateCategory,
  MessageTemplateSpecificity,
} from "src/constants";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { EditFormTemplateActionBar } from "src/scenes/orgAdmin/formTemplates/components/ActionBar";
import { FormTemplateDefaultCard } from "src/scenes/orgAdmin/formTemplates/components/FormTemplateDefaultCard";
import {
  FormTemplateId,
  hasChanges,
  toOriginal,
} from "src/scenes/orgAdmin/formTemplates/types/common";
import { maybePluralize } from "src/services/format";
import { MessageTemplate } from "src/services/url/OrgAdmin";
import * as GQL from "src/types/graphql";
import { useListMessagesTemplateContext } from "../context";
import { useSaveListChanges } from "../hooks/useSaveListChanges";
import { ListMessageTemplatesFormType, MessageTemplateType } from "../schemas";
import { SchoolSpecificSelector } from "./SchoolSpecificSelector";
import { StatusSwitch } from "./StatusSwitch";

const FORM_ID = "edit-form-templates-list-message-templates";

export type FormRef = {
  saveChanges: () => Promise<void>;
};

interface Props {
  messageTemplates: MessageTemplateType[];
  isRankingEnabled: boolean;
  isOfferEnabled: boolean;
  isSchoolSpecificMessagesEnabled: boolean;
  schoolList: GQL.GetSchoolsForEnrollmentPeriod_school[];
}

export const ListMessageTemplatesForm: FunctionComponent<Props> = ({
  messageTemplates,
  isRankingEnabled,
  isOfferEnabled,
  isSchoolSpecificMessagesEnabled,
  schoolList,
}) => {
  const organization = useOrganization().withDefault({} as GQL.Organization);
  const { formTemplateId = "", enrollmentPeriodId = "" } = useParams();
  const [searchParams, setSearchParams] = useSearchParamsRetainingHash();
  const initialSchoolId = searchParams.get("schoolId") ?? null;

  const { state, dispatch } = useListMessagesTemplateContext();

  const [selectedSchoolId, setSelectedSchoolId] = useState<string | null>(
    initialSchoolId ?? null
  );
  const [messageTemplateSpecificity, setMessageTemplateSpecificity] =
    useState<MessageTemplateSpecificity>(
      initialSchoolId
        ? MessageTemplateSpecificity.SchoolSpecific
        : MessageTemplateSpecificity.Standard
    );

  const toast = useAvelaToast();
  const saveListChanges = useSaveListChanges(
    formTemplateId,
    isRankingEnabled,
    dispatch
  );

  const handleSchoolIdChange = useCallback(
    (schoolId: string | null) => {
      setSelectedSchoolId(schoolId);
      const next = new URLSearchParams(searchParams);
      if (schoolId) {
        next.set("schoolId", schoolId);
      } else {
        next.delete("schoolId");
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  const handleSubmit = useCallback(
    async (values: ListMessageTemplatesFormType) => {
      try {
        await saveListChanges(values);

        amplitude.track("Clicked publish automatic messages", {
          organization_id: organization.id,
          enrollment_period_id: enrollmentPeriodId,
          form_template_id: formTemplateId,
        });

        toast({
          status: "info",
          title: "Automatic messages updated",
          isClosable: true,
        });
      } catch (error: unknown) {
        console.error(error);
        toast.genericError();
      }
    },
    [
      enrollmentPeriodId,
      formTemplateId,
      organization.id,
      saveListChanges,
      toast,
    ]
  );

  const handleValidate = useCallback(
    (values: ListMessageTemplatesFormType) => {
      if (dispatch) {
        dispatch({
          type: "UpdateListMessages",
          listMessages: {
            isDraft: true,
            ...values,
          },
          formTemplateId,
        });
      }
    },
    [formTemplateId, dispatch]
  );

  const draft = useMemo(
    () =>
      state?.get("listMessages")?.get(FormTemplateId(formTemplateId))?.draft,
    [formTemplateId, state]
  );

  const original = useMemo(
    () =>
      state?.get("listMessages")?.get(FormTemplateId(formTemplateId))?.original,
    [formTemplateId, state]
  );

  const formikInitialValues = useMemo(() => {
    if (draft) {
      return toOriginal(draft);
    } else {
      return original;
    }
  }, [draft, original]);

  const hasChangesResult = useMemo(
    () => hasChanges(original, draft),
    [original, draft]
  );

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: "Exit without saving?",
    body: "There are unsaved changes on this form. If you exit now, you will lose your changes.",
    confirmButton: { label: "Exit without saving" },
  });

  const handleResetForm = useCallback(() => {
    if (dispatch) {
      dispatch({
        type: "Reset",
        formTemplateId,
      });

      amplitude.track("Clicked reset automatic messages", {
        organization_id: organization.id,
        enrollment_period_id: enrollmentPeriodId,
        form_template_id: formTemplateId,
      });
    }
  }, [dispatch, enrollmentPeriodId, formTemplateId, organization.id]);

  if (!formikInitialValues) return <GenericError />;

  return (
    <Formik<ListMessageTemplatesFormType>
      initialValues={formikInitialValues}
      onSubmit={handleSubmit}
      validate={handleValidate}
      enableReinitialize
    >
      {({ resetForm, values }) => {
        const handleFormReset = () => {
          resetForm();
          handleResetForm();
        };

        return (
          <Flex direction="column" as={Form} id={FORM_ID} gap={4} mt={4}>
            {confirmationDialog}
            {MESSAGE_TEMPLATE_CATEGORIES.map((category) => (
              <CategorySection
                key={category.key}
                category={category}
                messageTemplates={messageTemplates}
                messageTemplateSpecificity={messageTemplateSpecificity}
                selectedSchoolId={selectedSchoolId}
                isSchoolSpecificMessagesEnabled={
                  isSchoolSpecificMessagesEnabled
                }
                isOfferEnabled={isOfferEnabled}
                schoolList={schoolList}
                organization={organization}
                enrollmentPeriodId={enrollmentPeriodId}
                formTemplateId={formTemplateId}
                values={values}
                hasUnsavedChanges={hasChangesResult}
                onConfirmNavigate={confirm}
                onSchoolIdChange={handleSchoolIdChange}
                onSpecificityChange={setMessageTemplateSpecificity}
              />
            ))}

            <EditFormTemplateActionBar
              formId={FORM_ID}
              hasChanges={hasChangesResult}
              onClearChanges={handleFormReset}
            />
          </Flex>
        );
      }}
    </Formik>
  );
};

// Extracted component for category sections
interface CategorySectionProps {
  category: (typeof MESSAGE_TEMPLATE_CATEGORIES)[0];
  messageTemplates: MessageTemplateType[];
  messageTemplateSpecificity: MessageTemplateSpecificity;
  selectedSchoolId: string | null;
  /** Whether school-specific messages are available (combines feature flag and enrollment setting) */
  isSchoolSpecificMessagesEnabled: boolean;
  isOfferEnabled: boolean;
  schoolList: GQL.GetSchoolsForEnrollmentPeriod_school[];
  organization: GQL.Organization;
  enrollmentPeriodId: string;
  formTemplateId: string;
  values: ListMessageTemplatesFormType;
  hasUnsavedChanges: boolean;
  onConfirmNavigate: () => Promise<boolean>;
  onSchoolIdChange: (schoolId: string | null) => void;
  onSpecificityChange: (specificity: MessageTemplateSpecificity) => void;
}

const CategorySection: FunctionComponent<CategorySectionProps> = ({
  category,
  messageTemplates,
  messageTemplateSpecificity,
  selectedSchoolId,
  isSchoolSpecificMessagesEnabled,
  isOfferEnabled,
  schoolList,
  organization,
  enrollmentPeriodId,
  formTemplateId,
  values,
  hasUnsavedChanges,
  onConfirmNavigate,
  onSchoolIdChange,
  onSpecificityChange,
}) => {
  const isLotteryAndOffersCategory =
    category.key === MessageTemplateCategory.LotteryAndOffers;
  const shouldShowSchoolSelector =
    isLotteryAndOffersCategory && isSchoolSpecificMessagesEnabled;

  const getStandardMessageSchoolCount = useCallback(
    (templateType: GQL.message_template_type_enum): number => {
      const schoolSpecificTemplates =
        values[templateType]?.schoolSpecific || {};
      const activeSchoolSpecificCount = Object.values(
        schoolSpecificTemplates
      ).filter((template) => template?.enabled).length;
      return schoolList.length - activeSchoolSpecificCount;
    },
    [schoolList.length, values]
  );

  const renderMessageTemplate = (
    messageTemplateType: GQL.message_template_type_enum,
    index: number
  ) => {
    const isOfferRelated = isLotteryAndOffersCategory;

    // Skip if offer related, school-specific, but school hasn't been selected yet
    if (
      isOfferRelated &&
      messageTemplateSpecificity ===
        MessageTemplateSpecificity.SchoolSpecific &&
      !selectedSchoolId
    ) {
      return null;
    }

    // Only apply schoolId if offer related and viewing school specific templates
    const schoolId =
      isOfferRelated &&
      messageTemplateSpecificity ===
        MessageTemplateSpecificity.SchoolSpecific &&
      selectedSchoolId
        ? selectedSchoolId
        : null;

    const messageTemplate = messageTemplates.find(
      (mt) =>
        mt.type === messageTemplateType &&
        mt.schoolId === (schoolId ?? undefined)
    );

    if (!messageTemplate) return null;

    // Disable editing if offer related but offer/lottery not yet enabled
    const isLotteryDisabled = isOfferRelated && !isOfferEnabled;

    // Disable toggling and previewing if offer related but offer/lottery not yet enabled,
    // or if message doesn't exist yet
    const isToggleDisabled = isLotteryDisabled || !messageTemplate.id;

    const disabledReason = isLotteryDisabled
      ? LOTTERY_AND_OFFERS_DISABLED_MESSAGE
      : "To activate this message, first create and publish it using the Edit option";

    let footerInfo = "";
    if (
      !isToggleDisabled &&
      isSchoolSpecificMessagesEnabled &&
      isLotteryAndOffersCategory &&
      messageTemplateSpecificity === MessageTemplateSpecificity.Standard
    ) {
      const count = getStandardMessageSchoolCount(messageTemplate.type);
      footerInfo = `${count} ${maybePluralize(
        count,
        "school"
      )} ${maybePluralize(
        count,
        "is",
        "are"
      )} using this standard message, out of ${
        schoolList.length
      } total ${maybePluralize(schoolList.length, "school")}.`;

      if (!messageTemplate.enabled && count < schoolList.length) {
        footerInfo += " Some school specific messages may still be enabled.";
      }
    }

    return (
      <MessageTemplateCard
        key={index}
        messageTemplate={messageTemplate}
        messageTemplateType={messageTemplateType}
        category={category}
        schoolId={schoolId}
        isOfferRelated={isOfferRelated}
        isLotteryDisabled={isLotteryDisabled}
        isToggleDisabled={isToggleDisabled}
        disabledReason={disabledReason}
        footerInfo={footerInfo}
        organization={organization}
        enrollmentPeriodId={enrollmentPeriodId}
        formTemplateId={formTemplateId}
        values={values}
        hasUnsavedChanges={hasUnsavedChanges}
        onConfirmNavigate={onConfirmNavigate}
      />
    );
  };

  return (
    <Flex direction="column" gap={4} key={category.key}>
      <Text fontSize="1.25rem" fontWeight="600">
        {category.label}
      </Text>
      {shouldShowSchoolSelector && (
        <SchoolSpecificSelector
          isSchoolSpecificMessagesEnabled={isSchoolSpecificMessagesEnabled}
          messageTemplateSpecificity={messageTemplateSpecificity}
          selectedSchoolId={selectedSchoolId}
          schoolList={schoolList}
          setMessageTemplateSpecificity={onSpecificityChange}
          setSelectedSchoolId={onSchoolIdChange}
        />
      )}

      {category.messageTemplateTypes.map(renderMessageTemplate)}
    </Flex>
  );
};
// Extracted component for individual message template cards
interface MessageTemplateCardProps {
  messageTemplate: MessageTemplateType;
  messageTemplateType: GQL.message_template_type_enum;
  category: (typeof MESSAGE_TEMPLATE_CATEGORIES)[0];
  schoolId: string | null;
  isOfferRelated: boolean;
  isLotteryDisabled: boolean;
  isToggleDisabled: boolean;
  disabledReason: string;
  footerInfo: string;
  organization: GQL.Organization;
  enrollmentPeriodId: string;
  formTemplateId: string;
  values: ListMessageTemplatesFormType;
  hasUnsavedChanges: boolean;
  onConfirmNavigate: () => Promise<boolean>;
}

const MessageTemplateCard: FunctionComponent<MessageTemplateCardProps> = ({
  messageTemplate,
  messageTemplateType,
  category,
  schoolId,
  isOfferRelated,
  isLotteryDisabled,
  isToggleDisabled,
  disabledReason,
  footerInfo,
  organization,
  enrollmentPeriodId,
  formTemplateId,
  values,
  hasUnsavedChanges,
  onConfirmNavigate,
}) => {
  const navigate = useNavigate();

  const statusSwitch = (
    <StatusSwitch
      key={`${messageTemplate.type}-${schoolId || "standard"}`}
      templateType={messageTemplate.type}
      schoolId={schoolId}
      disabled={isToggleDisabled}
      onToggle={(value: boolean) => {
        amplitude.track("Clicked automatic message active status", {
          organization_id: organization.id,
          enrollment_period_id: enrollmentPeriodId,
          form_template_id: formTemplateId,
          message_template_id: messageTemplate.id,
          message_template_type: messageTemplate.type,
          school_id: schoolId || "standard",
          is_active: value,
        });
      }}
    />
  );

  const renderActionSwitch = () => {
    if (isToggleDisabled) {
      return (
        <Tooltip
          label={<Text align="center">{disabledReason}</Text>}
          hasArrow
          placement="top"
          borderRadius="md"
          w={250}
          p={2}
        >
          <Flex>{statusSwitch}</Flex>
        </Tooltip>
      );
    }
    return statusSwitch;
  };

  const renderTag = () => {
    if (!isOfferRelated) return null;

    return (
      <Tag
        size="sm"
        variant="solid"
        color="blackAlpha.900"
        bg="gray.200"
        width="fit-content"
        fontSize="xs"
      >
        Lottery & offers
      </Tag>
    );
  };

  const editRoute = MessageTemplate.edit(
    organization,
    enrollmentPeriodId,
    formTemplateId,
    messageTemplate.type,
    schoolId,
    false
  );

  const previewRoute = MessageTemplate.edit(
    organization,
    enrollmentPeriodId,
    formTemplateId,
    messageTemplate.type,
    schoolId,
    true
  );

  const onEditClick = useCallback(async () => {
    if (hasUnsavedChanges) {
      const confirmed = await onConfirmNavigate();
      if (!confirmed) {
        return;
      }
    }
    navigate(editRoute);

    amplitude.track("Clicked edit automatic message", {
      organization_id: organization.id,
      enrollment_period_id: enrollmentPeriodId,
      form_template_id: formTemplateId,
      message_template_id: messageTemplate.id,
      message_template_type: messageTemplate.type,
      school_id: schoolId ?? "standard",
    });
  }, [
    hasUnsavedChanges,
    navigate,
    editRoute,
    organization.id,
    enrollmentPeriodId,
    formTemplateId,
    messageTemplate.id,
    messageTemplate.type,
    schoolId,
    onConfirmNavigate,
  ]);

  const onPreviewClick = useCallback(async () => {
    if (hasUnsavedChanges) {
      const confirmed = await onConfirmNavigate();
      if (!confirmed) {
        return;
      }
    }
    navigate(previewRoute);

    amplitude.track("Clicked preview automatic message", {
      organization_id: organization.id,
      enrollment_period_id: enrollmentPeriodId,
      form_template_id: formTemplateId,
      message_template_id: messageTemplate.id,
      message_template_type: messageTemplate.type,
      school_id: schoolId || "standard",
    });
  }, [
    hasUnsavedChanges,
    navigate,
    previewRoute,
    organization.id,
    enrollmentPeriodId,
    formTemplateId,
    messageTemplate.id,
    messageTemplate.type,
    schoolId,
    onConfirmNavigate,
  ]);

  return (
    <FormTemplateDefaultCard
      title={MESSAGE_TEMPLATES[messageTemplate.type].label}
      footerInfo={footerInfo}
      onPreviewClick={onPreviewClick}
      onEditClick={onEditClick}
      tag={renderTag()}
      templateDisabled={isLotteryDisabled}
      actionSwitch={renderActionSwitch()}
    />
  );
};
